/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import { put, call, takeLeading, select, all, fork } from 'redux-saga/effects';
import { SagaIterator } from 'redux-saga';
import {
  ActionRequest,
  ActionFail,
  ActionSuccess,
  XError,
  Asset,
  PersonalList,
  WishlistUtils,
  AsyncActionState,
  PartnerListTypeType,
  FileFormatToFileTypeMap,
  ContentProps,
  PersonalListApi,
  Configs,
  AssetMediaType,
  TimeUtils,
  WishlistErrors,
  WishlistOption,
  ParentalControlSettings,
  HideUnsubscribed,
  FolderConfigs,
  Ducks,
  ActionsEventActionNames,
  WishlistApi,
} from '@wit/vtv-x-foundation-lib';
import { StoreState } from 'store/store';
import AppConfig from '../../../services/AppConfig';
import { addWishlistActionEvent } from '../../../store/ducks/analytics/actionsEvent';

export enum ActionType {
  GET_ITEMS = 'wishlist/GET_ITEMS',
  GET_ITEMS_SUCCESS = 'wishlist/GET_ITEMS_SUCCESS',
  GET_ITEMS_ERROR = 'wishlist/GET_ITEMS_ERROR',
  UPDATE_ITEMS = 'wishlist/UPDATE_ITEMS',
  ADD_ITEM = 'wishlist/ADD_ITEM',
  ADD_ITEM_SUCCESS = 'wishlist/ADD_ITEM_SUCCESS',
  ADD_ITEM_ERROR = 'wishlist/ADD_ITEM_ERROR',
  DEL_ITEM = 'wishlist/DEL_ITEM',
  DEL_ITEM_SUCCESS = 'wishlist/DEL_ITEM_SUCCESS',
  DEL_ITEM_ERROR = 'wishlist/DEL_ITEM_ERROR',
}

type GetItems = ActionRequest<ActionType.GET_ITEMS>;
type GetItemsError = ActionFail<ActionType.GET_ITEMS_ERROR>;
type GetItemsSuccess = ActionSuccess<ActionType.GET_ITEMS_SUCCESS, WishlistData>;
type UpdateItems = ActionSuccess<ActionType.UPDATE_ITEMS, WishlistData>;

type Actions = GetItems | GetItemsError | GetItemsSuccess | UpdateItems;

const Actions = {
  getItems: (): GetItems => ({ type: ActionType.GET_ITEMS }),
  getItemsSuccess: (payload: WishlistData): GetItemsSuccess => ({
    type: ActionType.GET_ITEMS_SUCCESS,
    payload,
  }),
  getItemsError: (error: XError): GetItemsError => ({ type: ActionType.GET_ITEMS_ERROR, error }),
  updateItems: (payload: WishlistData): UpdateItems => ({ type: ActionType.UPDATE_ITEMS, payload }),
};

export const ActionCreators = {
  getItems: Actions.getItems,
};

// SELECTORS
export const Selectors = {
  getAssets: (state: StoreState): Asset[] => state.wishlist.data.assets,
  getPersonalLists: (state: StoreState): PersonalList[] => state.wishlist.data.personalLists,
  isAssetWishlisted: (state: StoreState, asset: Asset): boolean =>
    state.wishlist.data.personalLists.find((personalList) =>
      WishlistUtils.assetMatchsPersonalList(asset, personalList)
    ) !== undefined,
  isGetItemsProcessed: (state: StoreState): boolean | undefined => state.wishlist.actions.getItems?.processed,
};

// REDUCER
type WishlistData = {
  assets: Asset[];
  personalLists: PersonalList[];
};

export type ReducerState = {
  data: WishlistData;
  actions: {
    getItems?: AsyncActionState<undefined>;
  };
};

const initialState: ReducerState = { data: { assets: [], personalLists: [] }, actions: {} };

export default function wishlist(state: ReducerState = initialState, action: Actions): ReducerState {
  switch (action.type) {
    case ActionType.GET_ITEMS:
      return {
        ...state,
        actions: {
          ...state.actions,
          getItems: { processing: true, processed: false, error: undefined, data: undefined },
        },
      };
    case ActionType.GET_ITEMS_SUCCESS:
      return {
        ...state,
        actions: {
          ...state.actions,
          getItems: { processing: false, processed: true, error: undefined, data: undefined },
        },
        data: action.payload,
      };
    case ActionType.GET_ITEMS_ERROR:
      return {
        ...state,
        actions: {
          ...state.actions,
          getItems: { processing: false, processed: true, error: action.error, data: undefined },
        },
      };
    case ActionType.UPDATE_ITEMS:
      return {
        ...state,
        data: action.payload,
      };
    default:
      return state;
  }
}

function* getPersonalListsAndSingleAssets(): SagaIterator {
  const filesFormat: FileFormatToFileTypeMap = yield select(Ducks.Dms.Selectors.getFilesFormat);
  const parentalControlSettings: ParentalControlSettings = yield select(Ducks.Dms.Selectors.getParentalControl);
  const folderConfigs: FolderConfigs | undefined = yield select(Ducks.Dms.Selectors.getFoldersConfig);
  const hideUnsubscribed = HideUnsubscribed[AppConfig.env.CHANNELS_UNSUBSCRIBED_VISIBILITY];

  return yield Ducks.Api.callApi(
    WishlistApi.getPersonalListsAndSingleAssets,
    filesFormat,
    parentalControlSettings,
    hideUnsubscribed,
    folderConfigs
  );
}

function* getSeriesAssets(seriesPersonalLists: PersonalList[]): SagaIterator {
  const filesFormat: FileFormatToFileTypeMap = yield select(Ducks.Dms.Selectors.getFilesFormat);
  const parentalControlSettings: ParentalControlSettings = yield select(Ducks.Dms.Selectors.getParentalControl);
  const folderConfigs: FolderConfigs | undefined = yield select(Ducks.Dms.Selectors.getFoldersConfig);
  const hideUnsubscribed = HideUnsubscribed[AppConfig.env.CHANNELS_UNSUBSCRIBED_VISIBILITY];

  return yield Ducks.Api.callApi(
    WishlistApi.getSeriesAssets,
    seriesPersonalLists,
    filesFormat,
    parentalControlSettings,
    hideUnsubscribed,
    folderConfigs
  );
}

const mapSeriesPersonListsToAssets = (
  personalLists: PersonalList[],
  assets: Asset[][]
): [Map<number, Asset>, number[]] => {
  const personalListAssetMap = new Map<number, Asset>();
  const notMatchedPersonalLists: number[] = [];

  for (let i = 0; i < assets.length; i += 1) {
    if (assets[i].length) {
      personalListAssetMap.set(personalLists[i].id, assets[i][0]);
    } else {
      notMatchedPersonalLists.push(personalLists[i].id);
    }
  }

  return [personalListAssetMap, notMatchedPersonalLists];
};

const mapSinglePersonalListsToAssets = (
  personalLists: PersonalList[],
  assets: Asset[]
): [Map<number, Asset>, number[]] => {
  const personalListAssetMap = new Map<number, Asset>();
  const notMatchedPersonalLists: number[] = [];

  const uniqueAssetsMap = new Map<string, Asset>();
  // internalId: program, movie, recording
  // id: folders
  assets.forEach((asset) => uniqueAssetsMap.set((asset as ContentProps).internalId || asset.id.toString(), asset));

  let asset;
  personalLists.forEach((personalList) => {
    asset = uniqueAssetsMap.get(personalList.name);
    if (asset) {
      personalListAssetMap.set(personalList.id, asset);
    } else {
      notMatchedPersonalLists.push(personalList.id);
    }
  });

  return [personalListAssetMap, notMatchedPersonalLists];
};

// SAGAS

function* deletePersonalLists(ids: number[]): SagaIterator {
  try {
    const delRequests = ids.map((id) => Ducks.Api.callApi(PersonalListApi.delete, id));

    yield all(delRequests);
  } catch {
    // do nothing
    // this is a silent and cleanup task, it's not important report errors
  }
}

function* getItemsSaga(): SagaIterator {
  try {
    const allPersonalListsAndSingles: [PersonalList[], Asset[]] = yield call(getPersonalListsAndSingleAssets);
    const [allPersonalLists, singleAssets] = allPersonalListsAndSingles;

    const seriesPersonalLists = allPersonalLists.filter(
      (personalList) => personalList.partnerListType === PartnerListTypeType.SERIES
    );

    const seriesAssets: Asset[][] = seriesPersonalLists.length ? yield call(getSeriesAssets, seriesPersonalLists) : [];

    const [seriesPersonListsMap, notMatchedSeries] = mapSeriesPersonListsToAssets(seriesPersonalLists, seriesAssets);

    const singlePersonalLists = allPersonalLists.filter(
      (personalList) => personalList.partnerListType === PartnerListTypeType.SINGLE_EVENTS
    );

    const [singlePersonListsMap, notMatchedSingles] = mapSinglePersonalListsToAssets(singlePersonalLists, singleAssets);

    const items: WishlistData = { assets: [], personalLists: [] };

    allPersonalLists.forEach((personalList) => {
      const asset = singlePersonListsMap.get(personalList.id) || seriesPersonListsMap.get(personalList.id);
      if (asset) {
        items.assets.push(asset);
        items.personalLists.push(personalList);
      }
    });

    yield put(Actions.getItemsSuccess(items));

    yield fork(deletePersonalLists, [...notMatchedSeries, ...notMatchedSingles]);
  } catch (error) {
    yield put(Actions.getItemsError(XError.fromError(error)));
  }
}

function* addItem(asset: Asset): SagaIterator {
  try {
    const configs: Configs = yield select(Ducks.Dms.Selectors.getDmsConfigs);

    const name = WishlistUtils.getPersonalListName(asset);

    const mediaTypeId = configs.mediaTypesIds.get(AssetMediaType.SERIES);
    const kSql = WishlistUtils.getKSql(
      asset,
      TimeUtils.daysToSecs(configs.epgBackwardDays),
      TimeUtils.daysToSecs(configs.epgForwardDays),
      mediaTypeId as number
    );

    if (kSql) {
      const personalList: PersonalList = yield Ducks.Api.callApi(
        PersonalListApi.add,
        name,
        kSql,
        WishlistUtils.getPartnerListTypeType(asset)
      );

      const storedPersonalLists: PersonalList[] = yield select(Selectors.getPersonalLists);
      const storedAssets: Asset[] = yield select(Selectors.getAssets);

      yield put(
        Actions.updateItems({
          assets: [asset, ...storedAssets],
          personalLists: [personalList, ...storedPersonalLists],
        })
      );
      yield fork(addWishlistActionEvent, ActionsEventActionNames.WISHLIST_ADD, asset);
    }
  } catch {
    const error = XError.fromCode(WishlistErrors.generic.add);
    yield fork(addWishlistActionEvent, ActionsEventActionNames.WISHLIST_ADD, asset, error);
    throw error;
  }
}

function* delItem(asset: Asset): SagaIterator {
  try {
    const storedPersonalLists: PersonalList[] = yield select(Selectors.getPersonalLists);
    const storedAssets: Asset[] = yield select(Selectors.getAssets);

    const delIds = [];
    const nonDeletedPersonalLists = [];
    const nonDeletedAssets = [];

    for (let i = 0; i < storedPersonalLists.length; i += 1) {
      if (WishlistUtils.assetMatchsPersonalList(asset, storedPersonalLists[i])) {
        delIds.push(storedPersonalLists[i].id);
      } else {
        nonDeletedPersonalLists.push(storedPersonalLists[i]);
        nonDeletedAssets.push(storedAssets[i]);
      }
    }

    yield call(deletePersonalLists, delIds);

    yield put(
      Actions.updateItems({
        assets: nonDeletedAssets,
        personalLists: nonDeletedPersonalLists,
      })
    );
    yield fork(addWishlistActionEvent, ActionsEventActionNames.WISHLIST_REMOVE, asset);
  } catch {
    const error = XError.fromCode(WishlistErrors.generic.remove);
    yield fork(addWishlistActionEvent, ActionsEventActionNames.WISHLIST_REMOVE, asset, error);
    throw error;
  }
}

function* toggleItem(option: WishlistOption): SagaIterator {
  const operation = option.isWishlisted ? delItem : addItem;

  yield call(operation, option.asset as Asset);
}

export const SagaFunctions = {
  toggleItem,
};

export function* saga(): SagaIterator {
  yield all([takeLeading([ActionType.GET_ITEMS, Ducks.Credentials.ActionTypes.SWITCH_USER_SUCCESS], getItemsSaga)]);
}
